import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import { PlusIcon } from '@heroicons/react/20/solid'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { ButtonGroup, ButtonGroupItem } from '../lib/button-group'
import { useFarms } from '../lib/farms'
import { CATEGORY_LABEL, inventoryPath, STOCK_CATEGORY_LABEL, stockPath, useFarmAssets, useFarmStock, type InventoryTab } from '../lib/inventory'
import { formatAmount, packageBreakdown } from '../lib/stock-units'
import type { AssetCategory } from '../lib/types'
import { Shell } from './shell'

// The farm's inventory: the equipment and buildings its records are kept against, and its stock of
// pesticides, fertilizers and other inputs. Movable equipment (tractors, sprayers ...) belongs to the
// whole organization, so it shows on every farm's page; stock is each farm's own. Each asset and
// stock item, and adding or editing one, has a page of its own (asset-page, stock-item-page ...).

const TABS: { tab: InventoryTab; label: string }[] = [
  { tab: 'EQUIPMENT', label: CATEGORY_LABEL.EQUIPMENT.plural },
  { tab: 'BUILDING', label: CATEGORY_LABEL.BUILDING.plural },
  { tab: 'STOCK', label: 'Stock' },
]

const Placeholder = ({ children, failed }: { children: React.ReactNode; failed?: boolean }) => (
  <div className={`rounded-lg border border-dashed p-10 text-center ${failed ? 'border-red-300 dark:border-red-900' : 'border-zinc-950/15 dark:border-white/15'}`}>
    <Text>{children}</Text>
  </div>
)

function AssetList({ orgId, farmId, category, canWrite }: { orgId: string; farmId: string; category: AssetCategory; canWrite: boolean }) {
  const { assets, loading, error } = useFarmAssets(orgId, farmId, category)
  const label = CATEGORY_LABEL[category]
  if (error) return <Placeholder failed>{error}</Placeholder>
  if (loading) return <Placeholder>Loading…</Placeholder>
  if (assets.length === 0) {
    return (
      <Placeholder>
        No {label.plural.toLowerCase()} yet.{canWrite ? ` Add the first ${label.singular} to start keeping records against it.` : ''}
      </Placeholder>
    )
  }
  return (
    // A list rather than a table: two lines per asset read well at phone width, with no sideways scroll.
    <ul className="divide-y divide-zinc-950/5 border-y border-zinc-950/5 dark:divide-white/5 dark:border-white/5">
      {assets.map((asset) => (
        <li key={asset.id}>
          <Link href={`/farms/${farmId}/inventory/${asset.id}`} className="flex items-center gap-4 py-4 data-hover:bg-zinc-950/2.5 dark:data-hover:bg-white/2.5">
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2 text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">
                {asset.name}
                {asset.kind === 'MOVABLE' && <Badge color="zinc">All farms</Badge>}
              </span>
              <span className="block truncate text-sm/6 text-zinc-500 dark:text-zinc-400">{[asset.type, asset.makeModel].filter(Boolean).join(' · ')}</span>
            </span>
            <ChevronRightIcon className="size-4 shrink-0 text-zinc-400" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

function StockList({ orgId, farmId, canWrite }: { orgId: string; farmId: string; canWrite: boolean }) {
  const { items, loading, error } = useFarmStock(orgId, farmId)
  if (error) return <Placeholder failed>{error}</Placeholder>
  if (loading) return <Placeholder>Loading…</Placeholder>
  if (items.length === 0) {
    return <Placeholder>No stock yet.{canWrite ? ' Add the pesticides, fertilizers and other inputs this farm keeps, and how much is on hand.' : ''}</Placeholder>
  }
  return (
    <ul className="divide-y divide-zinc-950/5 border-y border-zinc-950/5 dark:divide-white/5 dark:border-white/5">
      {items.map((item) => (
        <li key={item.id}>
          <Link href={stockPath(farmId, item.id)} className="flex items-center gap-4 py-4 data-hover:bg-zinc-950/2.5 dark:data-hover:bg-white/2.5">
            <span className="min-w-0 flex-1">
              <span className="block text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">{item.name}</span>
              <span className="block truncate text-sm/6 text-zinc-500 dark:text-zinc-400">
                {[STOCK_CATEGORY_LABEL[item.category], item.product && `PCP ${item.product.pcpNumber}`].filter(Boolean).join(' · ')}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className={`block text-base/6 font-medium tabular-nums sm:text-sm/6 ${item.onHand < 0 ? 'text-red-600 dark:text-red-400' : 'text-zinc-950 dark:text-white'}`}>
                {formatAmount(item, item.onHand)}
              </span>
              {packageBreakdown(item) && <span className="block text-sm/6 text-zinc-500 dark:text-zinc-400">{packageBreakdown(item)}</span>}
            </span>
            <ChevronRightIcon className="size-4 shrink-0 text-zinc-400" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

export function InventoryPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { getFarm, loading: farmsLoading } = useFarms()
  const tab: InventoryTab = TABS.find((t) => t.tab === params.get('category'))?.tab ?? 'EQUIPMENT'
  const canWrite = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined
  if (!farmsLoading && !farm) return <Navigate to="/" replace />
  if (!farm || !org || !farmId) return null

  // Links from before assets had their own page opened a dialog with ?asset=<id>.
  const legacyAsset = params.get('asset')
  if (legacyAsset) return <Navigate to={`/farms/${farmId}/inventory/${legacyAsset}`} replace />

  const add =
    tab === 'STOCK'
      ? { href: `${stockPath(farmId)}/new`, label: 'Add stock item' }
      : { href: `/farms/${farmId}/inventory/new${tab === 'BUILDING' ? '?category=BUILDING' : ''}`, label: `Add ${CATEGORY_LABEL[tab].singular}` }

  return (
    <Shell>
      <Link href={`/farms/${farmId}`} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {farm.name}
      </Link>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Heading>Inventory</Heading>
          <Text>
            {tab === 'STOCK'
              ? 'The pesticides, fertilizers and other inputs this farm keeps, and how much is on hand.'
              : "What this farm's records are kept against. Pick an item by name when you log its maintenance or inspection."}
          </Text>
        </div>
        {canWrite && (
          <Button href={add.href}>
            <PlusIcon />
            {add.label}
          </Button>
        )}
      </div>

      <div className="mt-6">
        <ButtonGroup>
          {TABS.map((t) => (
            <ButtonGroupItem key={t.tab} current={t.tab === tab} onClick={() => navigate(inventoryPath(farmId, t.tab), { replace: true })}>
              {t.label}
            </ButtonGroupItem>
          ))}
        </ButtonGroup>
      </div>

      <div className="mt-6">
        {tab === 'STOCK' ? (
          <StockList orgId={org.id} farmId={farmId} canWrite={canWrite} />
        ) : (
          <AssetList key={tab} orgId={org.id} farmId={farmId} category={tab} canWrite={canWrite} />
        )}
      </div>
    </Shell>
  )
}
